import { inject, Injectable } from '@angular/core';
import {
  catchError,
  first,
  forkJoin,
  from,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  tap,
  toArray,
} from 'rxjs';
import { DEFAULT_CURRENCY } from 'src/app/config/app.config';
import { IDrawer } from '../../interfaces/drawer.interface';
import { ILocation } from '@core/interfaces/location_legacy.interface';
import { Logger } from '../../interfaces/Logger';
import { IOrganization } from '../organization/domain/interfaces/organization.interface';
import { IWaiter } from '../../interfaces/waiter.interface';
import { OrganizationRepositoryAdapter } from '../organization/infrastructure/adapters/organization-repository.adapter';
import { Account } from './domain/model/account.model';
import { AccountEntityMapper } from './infrastructure/mappers/account-entity.mapper';
import { IAccount } from './infrastructure/interfaces/account-storage.interface';

import { SERVICE_STATE_SERVICE } from '@features/service/domain/tokens/service-state.token';
import { LocationMapper } from '@features/service/infrastructure/mappers/location.mapper';
import { DrawerMapper } from '@features/service/infrastructure/mappers/drawer.mapper';
import { WorkerMapper } from '@features/service/infrastructure/mappers/worker.mapper';

@Injectable({
  providedIn: 'root',
})
export class AccountFactoryService {
  private readonly organizationService = inject(OrganizationRepositoryAdapter);
  private readonly logger = inject(Logger);

  private readonly serviceStateService = inject(SERVICE_STATE_SERVICE);
  private readonly locationMapper = inject(LocationMapper);
  private readonly drawerMapper = inject(DrawerMapper);
  private readonly workerMapper = inject(WorkerMapper);

  waitersCache!: Map<string, IWaiter>;
  locationsCache!: Map<string, ILocation>;
  drawersCache!: Map<string, IDrawer>;
  currentOrganization!: IOrganization | null;

  createMany(accounts: IAccount[]): Observable<Account[]> {
    return this.preloadAccountRelations().pipe(
      first(),
      switchMap(() =>
        from(accounts).pipe(
          mergeMap((account) => {
            return this.createOne(account).pipe(
              catchError((error) => {
                this.logger.error(
                  new Error(
                    `[AccountFactory][createMany] Error creating one account ${account?._id}`
                  ),
                  error
                );

                return of(null);
              })
            );
          }),
          toArray(),
          map(
            (results) =>
              results.filter((ticket) => ticket !== null) as Account[]
          )
        )
      ),
      tap(() => this.removePreloadCache())
    );
  }

  createOne(account: IAccount, singleFetch = false): Observable<Account> {
    let preload$: Observable<void> = of(undefined);

    if (singleFetch) {
      preload$ = this.preloadAccountRelations();
    }

    return preload$.pipe(
      map(() => {
        if (!account) {
          this.logger.error(
            new Error('[AccountFactory][createOne] Account is null or undefined')
          );
        }

        let waiter = null;

        if (account?.waiter_id) {
          waiter = this.waitersCache?.get(account.waiter_id) || null;
        }

        let location = null;

        if (account?.location_id) {
          location = this.locationsCache?.get(account.location_id) || null;
        }

        let drawer = null;

        if (account?.cash_drawer_id) {
          drawer = this.drawersCache?.get(account.cash_drawer_id) || null;
        }

        const organization = this.currentOrganization;

        return this.create(account, location, drawer, waiter, organization);
      }),
      tap(() => {
        if (singleFetch) {
          this.removePreloadCache();
        }
      })
    );
  }
  /**
   * Creates a new account in the factory. Then it assigns external info to it
   *
   * @param {IAccount} item
   * @return {*}
   * @memberof AccountFactoryService
   */
  private create(
    item: IAccount,
    location: ILocation | null,
    drawer: IDrawer | null,
    waiter: IWaiter | null,
    organization: IOrganization | null
  ): Account {
    const account = AccountEntityMapper.fromStorage(item);

    account.updateDrawer(drawer);
    account.updateLocation(location);
    account.updateWaiter(waiter || null);
    account.updatePriceCurrency(organization?.currency || DEFAULT_CURRENCY);

    return account;
  }

  private preloadAccountRelations(): Observable<void> {
    return forkJoin({
      workers: of(this.serviceStateService.getAllWorkers()),
      locations: of(this.serviceStateService.getAllLocations()),
      drawers: of(this.serviceStateService.getAllDrawers()),
      organization: this.organizationService.getCurrentOrganization(),
    }).pipe(
      first(),
      tap(({ workers, locations, drawers, organization }) => {
        const legacyWorkers = this.workerMapper.fromDomainArrayToLegacyApi(workers);

        this.waitersCache = new Map(
          legacyWorkers.map((worker) => [worker._id, worker])
        );

        const legacyLocations = this.locationMapper.fromDomainArrayToLegacyApi(locations);

        this.locationsCache = new Map(
          legacyLocations.map((location) => [location._id, location])
        );

        const legacyDrawers = this.drawerMapper.fromDomainArrayToLegacyApi(drawers);

        this.drawersCache = new Map(
          legacyDrawers.map((drawer) => [drawer._id, drawer])
        );
        this.currentOrganization = organization;
      }),
      map(() => void 0),
      catchError((error) => {
        this.logger.error(
          new Error('[AccountFactory][preloadAccountRelations] Error preloading relations'),
          error
        );

        return of(void 0);
      })
    );
  }

  private removePreloadCache(): void {
    this.waitersCache.clear();
    this.locationsCache.clear();
    this.drawersCache.clear();
    this.currentOrganization = null;
  }
}
